import React, {useRef} from 'react';
import './PinInputElement.css';

export type PinInputStage = 'new' | 'confirm';
export type PinBorderType = 'default' | 'highlight';

interface PinInputElementProps {
  stage: PinInputStage; // 'new' for initial entry, 'confirm' for confirmation
  values: string[];
  onChange: (idx: number, value: string) => void;
  autoFocusFirst?: boolean;
  borderType?: PinBorderType; // visual border style
  className?: string; // optional wrapper class
}

const PinInputElement: React.FC<PinInputElementProps> = ({
  stage,
  values,
  onChange,
  autoFocusFirst = false,
  borderType = 'default',
  className,
}) => {
  const isConfirm = stage === 'confirm';

  const inputRef1 = useRef<HTMLInputElement>(null);
  const inputRef2 = useRef<HTMLInputElement>(null);
  const inputRef3 = useRef<HTMLInputElement>(null);
  const inputRef4 = useRef<HTMLInputElement>(null);
  const inputsRef = [inputRef1, inputRef2, inputRef3, inputRef4];

  // Auto focus first input on mount/update when requested
  React.useEffect(() => {
    if (autoFocusFirst) {
      const nextInput = inputsRef[0].current;
      nextInput?.focus();
    }
  }, [autoFocusFirst, stage]);/* eslint-disable-line */

  const handleChange = (idx: number, value: string) => {
    if (!/^[0-9]?$/.test(value)) return;
    onChange(idx, value);
    if (value && idx < values.length - 1) {
      const nextInput = inputsRef[idx + 1].current;
      nextInput?.focus();
    }
  };

  const handleKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      const currentVal = values[idx];
      if (!currentVal && idx > 0) {
        const nextInput = inputsRef[idx - 1].current;
        nextInput?.focus();
      }
    }
  };

  const borderClass = borderType === 'highlight' ? 'pin-border-highlight' : 'pin-border-default';

  return (
    <div className={['reset-pin-inputs', className].filter(Boolean).join(' ')}>

      {values.map((digit, idx) => (
        <>
          <input
            key={idx}
            id={`pin-input-${idx}`}
            ref={inputsRef[idx]}
            type={isConfirm ? 'password' : 'text'}
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            className={[isConfirm ? 'reset-pin-input-confirm' : 'reset-pin-input', borderClass].join(' ')}
            value={digit}
            onChange={e => handleChange(idx, e.target.value)}
            onKeyDown={e => handleKeyDown(idx, e)}
            autoFocus={autoFocusFirst && idx === 0}
          />
        </>
      ))}
    </div>
  );
};

export default PinInputElement;
